// Thin command helpers — outbound `ClientEvent` constructors that go through
// the injected `Transport`. Keeping these as plain functions (not a class)
// because they hold no state of their own; the React layer composes them
// with the current `RoomState` / `PenConfig` it already owns.
//
// (architecture.md §10 / §16)

import type {
  CustomCursor,
  DeviceKind,
  LayoutKind,
  RoomJoinAuth,
  StrokePoint,
} from '@oekaki/protocol';
import { type PenConfig, penParamsFor } from '../domain/PenConfig.js';
import { noteOwnStrokeStart } from './ownStrokeActivity.js';
import type { Transport } from './ports/Transport.js';
import type { StrokeInputKind } from './strokeInput.js';

export interface JoinRoomOptions {
  roomId: string;
  displayName: string;
  isRom?: boolean;
  /**
   * Phase 7.5: room owner の admin_token を localStorage から拾った場合に
   * 渡す. 設定されていれば `auth.mode='admin'` を組み立て、未設定なら従来
   * 通り `auth.mode='visitor'` で送る.
   *
   * credential mode 経路 (Phase 6 で導入予定だが MVP web 側は未配線) は
   * 上位 (LoginPage 等) で別経路として join を呼ぶ想定で、本フィールドとは
   * 排他. SessionProvider の `join` は admin_token の有無だけ確認する.
   */
  adminToken?: string;
  /**
   * Phase 8 Task 8-5 (定着率メトリクス計装): 同一ユーザ識別子 (UUID v4).
   * `AnonUidStore.getOrCreate()` の戻り値をそのまま渡す. `null` /
   * `undefined` のときはフィールドごと送らない (= protocol optional).
   * (analytics.md §1)
   */
  clientAnonId?: string | null;
  /**
   * Phase 8 Task 8-5 (定着率メトリクス計装): `?ref=...` URL クエリの slug
   * (招待リンク識別子, 1..64 chars). `readReferrerFromCurrentUrl()` の
   * 戻り値をそのまま渡す. `null` / `undefined` のときはフィールドごと
   * 送らない. (analytics.md §3.1)
   */
  referrer?: string | null;
  /**
   * A5 (アナリティクス計装修理, analytics-operations-2026-08-13.md §2 表 A5):
   * `infrastructure/clientBuild.ts` の `getClientBuild()` の戻り値をそのまま
   * 渡す. `referrer` と違い「入口計測」ではなく「どのビルドで動いていたか」
   * の telemetry なので、resume-fail fallback join でも毎回送ってよい
   * (SessionProvider 参照). `getClientBuild()` は常に非空文字列を返すため
   * 通常 undefined にはならないが、防御的に optional で受ける.
   */
  clientBuild?: string;
  /**
   * レイアウト利用計測 (pen-usage-analytics-2026-08-18.md §9): 入室時点で
   * client が表示している Classic / Modern レイアウト
   * (`LayoutPreferenceStore.get()` の戻り値をそのまま渡す)。`clientBuild` と
   * 同じ流儀の telemetry-only optional field — 不在なら payload から落とす
   * (server は `session.start.layout` に `null` を記録するだけで protocol は
   * 壊れない)。
   */
  layout?: LayoutKind;
  /**
   * 端末区分の計測 (pen-usage-analytics-2026-08-18.md §11.2 / §11.3): 入室時点で
   * client が表示していたシェル + primary pointer (`deriveDeviceKind()` の戻り値
   * をそのまま渡す)。`layout` と同じ流儀の telemetry-only optional field —
   * 不在ならフィールドごと落とす (server は `session.start.device` /
   * `session.end.device` に `null` を記録するだけで protocol は壊れない)。
   */
  device?: DeviceKind;
  /**
   * 画面の拡大率 (`window.devicePixelRatio`、canvas-dot-by-dot-2026-10-28.md)。
   * telemetry-only optional field — 0 / 非有限 / 負ならフィールドごと落とす。
   */
  dpr?: number;
  /**
   * 「ドットバイドット表示」の設定 ('auto' | 'off')。telemetry-only optional field —
   * 不正値ならフィールドごと落とす。
   */
  dotByDot?: 'auto' | 'off';
  /**
   * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md). あいことば付き
   * 部屋の visitor 入室でのみ意味を持つ — `mode: 'admin'` (adminToken 経由)
   * のときはオーナーが打たないので黙って無視される (下の `joinRoom` が
   * visitor 分岐でのみ auth に載せる).
   * `undefined` / 空文字ならフィールドごと送らない.
   */
  entryPassword?: string;
}

/**
 * Issues `room.join`.
 *
 * - `adminToken` が指定されていれば `auth.mode='admin'` (Phase 7.5) で送る.
 *   server (`JoinService`) が dispatcher 経由で照合し、一致すれば
 *   `permissions = ALL_PERMS` を welcome に載せる.
 * - 指定されていなければ従来通り `auth.mode='visitor'`.
 *
 * credential mode は上位 (LoginPage 等) で `transport.send` を直接組み立てる
 * 経路を残す (本 helper は admin / visitor 2 系統の自動切替に集中).
 *
 * Phase 8 Task 8-5: `clientAnonId` / `referrer` を載せる. 両方とも optional
 * で server 側で raw 値は保管せず HMAC fingerprint 化される (analytics.md §1).
 * 不在 (null / undefined / 空文字) のときは payload からフィールドごと
 * 落とす — protocol layer で optional field の不在と空値は意味が違うため.
 */
export const joinRoom = (transport: Transport, opts: JoinRoomOptions): void => {
  const auth: RoomJoinAuth =
    opts.adminToken !== undefined && opts.adminToken.length > 0
      ? { mode: 'admin', adminToken: opts.adminToken, displayName: opts.displayName }
      : {
          mode: 'visitor',
          displayName: opts.displayName,
          // 入室あいことば: visitor 分岐でのみ載せる (admin は打たない、上の
          // 三項演算子で既にこちらの分岐に来ないので二重ガード不要).
          ...(opts.entryPassword !== undefined && opts.entryPassword.length > 0
            ? { entryPassword: opts.entryPassword }
            : {}),
        };
  // Phase 8 Task 8-5: 不在のフィールドは payload から完全に落とす. exactOptional
  // 設計意図: server schema が存在 + 空文字を別扱いしうる (現状は両方 reject だが、
  // 将来 optional 拡張があった時に副作用が出にくい).
  const clientAnonId =
    typeof opts.clientAnonId === 'string' && opts.clientAnonId.length > 0
      ? opts.clientAnonId
      : undefined;
  const referrer =
    typeof opts.referrer === 'string' && opts.referrer.length > 0 ? opts.referrer : undefined;
  // A5: same "field only present if non-empty" convention as clientAnonId /
  // referrer above.
  const clientBuild =
    typeof opts.clientBuild === 'string' && opts.clientBuild.length > 0
      ? opts.clientBuild
      : undefined;
  // pen-usage-analytics-2026-08-18.md §9: layout も「不在ならフィールドごと
  // 落とす」流儀に揃える (undefined と空値を区別しない — LayoutKind は自由
  // 文字列ではなく固定 2 値の enum なので空文字ケースは存在しない)。
  const layout = opts.layout;
  // pen-usage-analytics-2026-08-18.md §11.2/§11.3: device も layout と同じ
  // 「不在ならフィールドごと落とす」流儀 (固定 3 値の enum なので空文字ケースは
  // 存在しない)。
  const device = opts.device;
  const dpr =
    typeof opts.dpr === 'number' && Number.isFinite(opts.dpr) && opts.dpr > 0
      ? opts.dpr
      : undefined;
  const dotByDot = opts.dotByDot === 'auto' || opts.dotByDot === 'off' ? opts.dotByDot : undefined;
  transport.send({
    t: 'room.join',
    roomId: opts.roomId,
    auth,
    isRom: opts.isRom ?? false,
    ...(clientAnonId !== undefined ? { clientAnonId } : {}),
    ...(referrer !== undefined ? { referrer } : {}),
    ...(clientBuild !== undefined ? { clientBuild } : {}),
    ...(layout !== undefined ? { layout } : {}),
    ...(device !== undefined ? { device } : {}),
    ...(dpr !== undefined ? { dpr } : {}),
    ...(dotByDot !== undefined ? { dotByDot } : {}),
  });
};

/** Issues `chat.send`. ROM clients are silently dropped server-side. */
export const sendChat = (transport: Transport, text: string): void => {
  transport.send({ t: 'chat.send', text });
};

/** Issues `member.rom`. */
export const setRom = (transport: Transport, rom: boolean): void => {
  transport.send({ t: 'member.rom', rom });
};

/** Issues `member.name-change`. */
export const changeName = (transport: Transport, displayName: string): void => {
  transport.send({ t: 'member.name-change', displayName });
};

/**
 * Issues `member.cursor-change` (custom-cursor-2026-09-19.md §2.3). Confirms
 * a self-drawn 24×24 cursor (or clears it with `cursor: null`). Server
 * throttles to once per 2s and silently drops excess / invalid requests, so
 * callers may fire this freely. Success is observable via the
 * `member.cursor-changed` broadcast (reflected into `RoomState.members[]`).
 */
export const changeCursor = (transport: Transport, cursor: CustomCursor | null): void => {
  transport.send({ t: 'member.cursor-change', cursor });
};

/**
 * Issues `member.cursor-draft` (custom-cursor-2026-09-19.md §2.3). Live
 * in-progress editor state, sent while the cursor editor is open — `cursor:
 * null` means editing ended/cancelled. Server throttles to 5/s; callers
 * (the cursor editor) are responsible for only calling this when the pixels
 * actually changed, throttled to the same rate client-side (§2.4).
 */
export const draftCursor = (transport: Transport, cursor: CustomCursor | null): void => {
  transport.send({ t: 'member.cursor-draft', cursor });
};

/**
 * Issues `admin.kick` (Phase 7 Task 7-4). Server validates `Perm.KICK`
 * permission on the caller; rejections come back as `admin.kick-rejected`
 * (no-permission / target-not-found / cannot-kick-self / cannot-kick-admin)
 * — see protocol-events.md §6. The kick UI itself (slash command, admin
 * panel button) lives in Task 7-9; this thin command exposes the WS hop.
 */
export const sendKick = (
  transport: Transport,
  args: { targetUid: string; reason?: string },
): void => {
  transport.send(
    args.reason !== undefined
      ? { t: 'admin.kick', targetUid: args.targetUid, reason: args.reason }
      : { t: 'admin.kick', targetUid: args.targetUid },
  );
};

/**
 * Issues `whiteboard.clear` (Phase 7 Task 7-5 / W3 Classic layers). Server
 * gates on `room.clearOpen || hasPerm(Perm.CLEAR)` and replies with
 * `system.error { code: 'no-permission' }` on failure. Success is observable
 * via the `whiteboard.clear` broadcast (the reducer renders a system line and
 * the drawing engine applies the wipe).
 *
 * `layerId` controls the clear scope (W3, consult 2026-05-26):
 *   - **omit** `layerId` → clear **every layer** + rotate the snapshot session
 *     (the legacy "全消し" behaviour).
 *   - pass a concrete `layerId` (Classic = 0/1/2) → clear **only that layer**;
 *     the session continues. The server now preserves `layerId` on the
 *     broadcast so every client clears the same layer.
 */
export const sendClear = (transport: Transport, layerId?: number): void => {
  transport.send(
    layerId !== undefined ? { t: 'whiteboard.clear', layerId } : { t: 'whiteboard.clear' },
  );
};

/**
 * Issues `whiteboard.background-set` (owner canvas background color, A-2
 * transport; docs/_wip/canvas-background-color-2026-06-20.md §4.3). Mirrors
 * `sendClear`: a thin command + server-stamped broadcast in one `t`.
 *
 * Server gates on `hasPerm(Perm.CLEAR)` alone (deliberately decoupled from
 * `room.clearOpen`) and replies with `system.error { code: 'no-permission' }`
 * on failure. Success is observable via the `whiteboard.background-set`
 * broadcast — the originator too applies the change on receipt (no optimistic
 * local apply), so the broadcast is the single source of truth.
 *
 * `rgb` is a 24-bit RGB int (`0..0xFFFFFF`); alpha is intentionally absent —
 * the server fixes the background opaque (255). The A-3 ■ swatch UI calls this.
 */
export const sendBackgroundSet = (transport: Transport, rgb: number): void => {
  transport.send({ t: 'whiteboard.background-set', rgb });
};

/**
 * Issues `stroke.cancel` (conflict-aware Undo, β Phase 10,
 * docs/_wip/undo-conflict-aware-2026-07-12.md / protocol-events.md §1.1).
 * Server validates 6 steps (owner / not-too-old / ended / not snapshot-
 * job-locked / no bbox-intersecting later stroke) and replies with either
 * the `stroke.cancelled` broadcast (success — every client applies a
 * bbox-local rewind via `CanvasDrawingEngine.ingestUndoBroadcast`, see
 * `@oekaki/drawing-core`'s `UndoBaselineStore`) or a unicast
 * `stroke.cancel-rejected { sid, reason, conflicts? }` to the requester
 * (routed to `engine.notifyCancelRejected` — see `SessionProvider`).
 *
 * `sid` is the stroke to cancel — the caller (UI, not built here) is
 * expected to source it from `engine.getCancellableStrokes()` (LIFO
 * presentation order; the server is the sole arbiter of actual eligibility).
 */
export const requestCancelStroke = (transport: Transport, sid: string): void => {
  transport.send({ t: 'stroke.cancel', sid });
};

/**
 * Issues `whiteboard.region-cancel` (範囲の移動・コピーの「元に戻す」、
 * docs/_wip/region-move-2026-10-15.md「未決・要検証」)。線の `stroke.cancel` と同じ tombstone 方式:
 * server が判定して `whiteboard.region-cancelled` を broadcast (成功) するか、requester に
 * `whiteboard.region-cancel-rejected` を unicast (拒否) する。取り消し済みへの再要求は server が
 * 無応答なので、呼び出し側は「待ち状態」を作らない。
 *
 * `regionSeq` は自分の `whiteboard.region` echo の server seq (`engine.getCancellableOps()` 由来)。
 */
export const requestCancelRegion = (transport: Transport, regionSeq: number): void => {
  transport.send({ t: 'whiteboard.region-cancel', regionSeq });
};

/**
 * Issues `whiteboard.restore-request` (過去キャンバス復元 = 「続きを描く」,
 * Slice 3; past-canvas-new-document-2026-07-05.md §4.1). Mirrors the
 * imagepaste command/event split: the server replies with either the
 * `whiteboard.restore` broadcast (success — every client reloads its canvas
 * from the seeded snapshot) or a unicast `whiteboard.restore-rejected
 * { reason }` to the requester.
 *
 * Server gates on `hasPerm(Perm.CLEAR)` ALONE — the `room.clearOpen` bypass
 * is deliberately NOT applied (§4.2: clearOpen は「誰でもまっさらから描き始めて
 * よい」という前向きの開放であり、部屋の履歴を遡って全員のキャンバスを書き換える
 * 復元には及ばない). The UI pre-gates with `canRestorePastCanvas` but the
 * server re-validates.
 *
 * `pastCanvasId` is a `room_past_canvases.id` as returned by the past-canvas
 * list endpoint (`GET /rooms/:roomId/past-canvases`).
 */
export const sendRestoreRequest = (transport: Transport, pastCanvasId: string): void => {
  transport.send({ t: 'whiteboard.restore-request', pastCanvasId });
};

/** Cleanly close the WS — no protocol-level "leave" message exists. */
export const leaveRoom = (transport: Transport): void => {
  transport.close(1000, 'leave');
};

// ─── whiteboard.imagepaste-* (3-stage flow) ──────────────────────────────
//
// Phase 7 Task 7-7: thin command helpers for the imagepaste protocol.
// The actual UI glue (file picker / drag-drop / File API + presigned PUT
// fetch / drawing engine `applyImage`) lands in Task 7-8.
//
// `width`/`height` are **required** (Task 7-7 で schema 変更): the server
// has no way to recover the native dimensions before the PUT, so the
// client must read them via File API and pass them through.

export interface SendImagepasteRequestArgs {
  /** MUST be one of `'image/png' | 'image/jpeg' | 'image/gif'` (server enum). */
  readonly contentType: 'image/png' | 'image/jpeg' | 'image/gif';
  /** Bytes; server enforces `MAX_IMAGEPASTE_BYTES` (5 MiB MVP). */
  readonly contentLength: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /**
   * Target layer to paste onto (imagepaste-layer bug A web-side wiring).
   * Mirrors `stroke.start.layerId`. Omit ⇒ server defaults to layer 0
   * (the schema's `.default(0)`), preserving pre-layer-id behaviour.
   */
  readonly layerId?: number;
}

/**
 * Issues `whiteboard.imagepaste-request`. Server replies with either
 * `whiteboard.imagepaste-token { uploadId, putUrl, expiresAt }` (success)
 * or `whiteboard.imagepaste-rejected { reason }` (failure). Both arrive
 * via the WS event stream — callers route them through React state /
 * Provider hooks (Task 7-8).
 */
export const sendImagepasteRequest = (
  transport: Transport,
  args: SendImagepasteRequestArgs,
): void => {
  transport.send({
    t: 'whiteboard.imagepaste-request',
    contentType: args.contentType,
    contentLength: args.contentLength,
    x: args.x,
    y: args.y,
    width: args.width,
    height: args.height,
    // Mirrors the schema's `.default(0)`: callers that don't track layers
    // (= legacy contexts) send 0, preserving pre-layer-id behaviour. When the
    // active layer is wired (ImagepasteOverlay confirm) its id rides here.
    layerId: args.layerId ?? 0,
  });
};

/**
 * Issues `whiteboard.imagepaste-confirm` — call this only after the
 * presigned PUT to S3 succeeded. Server HEADs the uploaded object and
 * either broadcasts `whiteboard.imagepaste { url, x, y, width, height }`
 * to the room or replies with `whiteboard.imagepaste-rejected`
 * (`upload-failed` / `invalid-upload`).
 */
export const sendImagepasteConfirm = (transport: Transport, uploadId: string): void => {
  transport.send({ t: 'whiteboard.imagepaste-confirm', uploadId });
};

/**
 * `stroke.start` constructor — the React canvas wrapper builds a
 * `PenConfig` from local UI state and hands it here. The drawing engine
 * stub (or real impl later) is responsible for *generating* the `sid`.
 */
export const sendStrokeStart = (
  transport: Transport,
  args: {
    sid: string;
    pen: PenConfig;
    /** pointerdown の `pointerType` (3 値のみ。それ以外は呼び出し側で undefined にする)。 */
    input?: StrokeInputKind | undefined;
    /** そのストロークに適用した筆圧設定が太さ/濃さのどちらかでオンか (§12.3)。 */
    pressure?: boolean | undefined;
  },
): void => {
  const { sid, pen, input, pressure } = args;
  noteOwnStrokeStart();
  // texture preset (texture-brush-design.md §11 step 3). `penParamsFor` returns
  // `{ kind:'texture', preset }` only while pen='texture', else undefined →
  // the field is omitted (back-compat, `oekaki-v1` preserved).
  const penParams = penParamsFor(pen);
  transport.send({
    t: 'stroke.start',
    sid,
    pen: pen.pen,
    color: pen.color,
    alpha: pen.alpha,
    width: pen.width,
    layerId: pen.layerId,
    mask: pen.mask,
    // modern-layout.md §9.9 — the local-send AA flag. `PenConfig.aa` carries the
    // per-stroke value: Classic は常に false (UI 非露出、wire byte-identical)、
    // Modern は aaPreference (既定 ON) が chrome 経由で pen.aa に乗る。
    aa: pen.aa ?? false,
    ...(penParams !== undefined ? { penParams } : {}),
    // 入力手段と筆圧オンの計測 (pen-usage-analytics §12)。optional: 旧 server は strip、
    // 新 server は数えて取り除く。input は 3 値以外なら載せない (空文字・未知値は送らない)。
    ...(input !== undefined ? { input } : {}),
    ...(pressure !== undefined ? { pressure } : {}),
  });
};

/** `stroke.segment` constructor. */
export const sendStrokeSegment = (
  transport: Transport,
  args: { sid: string; pts: readonly StrokePoint[] },
): void => {
  // `pts` is shaped by drawing-engine; we just pass it through.
  transport.send({ t: 'stroke.segment', sid: args.sid, pts: [...args.pts] });
};

/** `stroke.end` constructor. */
export const sendStrokeEnd = (transport: Transport, args: { sid: string }): void => {
  transport.send({ t: 'stroke.end', sid: args.sid });
};
